<template>
  <div class="design-picker">
    <div class="top">
      <div class="header">
        <h2>{{ $t('DESIGNS.MULTI_CREATION.HEADING') }}</h2>
        <p>{{ $t('DESIGNS.MULTI_CREATION.SUBHEADING') }}</p>
      </div>

      <div class="search-field">
        <input
          v-model="searchTerm"
          type="text"
          :class="{ filled: searchTerm }"
          :placeholder="$t('DESIGNS.LIST.PLACEHOLDER.SEARCH')"
          @keyup.enter="search"
          @blur="search"
        />
        <button
          class="icon-only-btn reset-link"
          @mousedown.prevent
          @click="clearSearch"
        >
          <Icon icon="close" />
        </button>
        <button class="icon-only-btn search-link" @click="search">
          <Icon icon="lens" />
        </button>
      </div>
    </div>

    <LoadingHeart v-if="loading" />
    <p v-else-if="loadFailed" class="empty" data-testid="mpa-load-error">
      {{ $t('GENERAL.ERROR.HEADING') }}
    </p>
    <template v-else-if="!designs.length">
      <p
        v-if="appliedSearchTerm"
        class="empty"
        data-testid="mpa-no-search-result"
      >
        {{ $t('DESIGNS.LIST.NO_SEARCH_RESULT') }}
      </p>
      <!-- Borrowed from the empty composition list: same journey, same statement. -->
      <div v-else class="empty" data-testid="mpa-empty">
        <p>{{ $t('PUBLISHING.LIST.EMPTY.HEADING') }}</p>
        <p class="text-sm">{{ $t('PUBLISHING.LIST.EMPTY.TEXT') }}</p>
      </div>
    </template>
    <template v-else>
      <ul class="design-list">
        <li v-for="design in designs" :key="design.id">
          <Tile
            :name="design.name || `${$t('DESIGNS.ID')} ${design.id}`"
            icon="download-file"
            :image="designImage(design)"
            :display-status="false"
            is-selectable
            always-show-checkbox
            clickable
            image-overlay-icon="accept"
            :selected="selection.isSelected(design.id)"
            :selection-disabled="!selection.canSelect(design.id)"
            @toggle-selection="selection.toggle(picked(design))"
            @on-click="selection.toggle(picked(design))"
          />
        </li>
      </ul>

      <Pagination
        id="mpa-design-pagination"
        :total="count"
        :page-size="PAGE_SIZE"
        :current-page="page"
        @page-change="onPageChange"
      />
    </template>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue';
import LoadingHeart from 'commons/LoadingHeart/LoadingHeart.vue';
import Pagination from 'commons/pagination/Pagination.vue';
import Tile from 'commons/tileView/Tile.vue';
import designService from 'commons/api/designService/designService';
import type {
  PickedDesign,
  useMultiPrintAreaSelection,
} from 'commons/composition/multiPrintAreaSelection';

type Design = {
  id: number;
  name?: string;
  resources: { href: string }[];
};

/**
 * The design list. Picking one places it on the first free print area — the arrangement itself is
 * shown and edited by `MultiPrintAreaPlacement`.
 *
 * Served by **design-core** — see `docs/publishing/multi-print-area-creation.md` §8, which also
 * explains why `offset` is a page number here rather than a record offset.
 */
const props = defineProps<{
  selection: ReturnType<typeof useMultiPrintAreaSelection>;
}>();

const selection = props.selection;

const PAGE_SIZE = 24;

const designs = ref<Design[]>([]);
const count = ref(0);
const page = ref(0);
const searchTerm = ref('');
const appliedSearchTerm = ref('');
const loading = ref(true);
const loadFailed = ref(false);

const designImage = (design: Design) =>
  `${design.resources[0]?.href},width=250,height=250`;

/** Only what the placement needs — the list itself is replaced on every page change. */
const picked = (design: Design): PickedDesign => ({
  id: design.id,
  name: design.name,
  href: design.resources[0]?.href,
});

const fetchDesigns = async () => {
  loading.value = true;
  loadFailed.value = false;

  try {
    const { designs: list, count: total } = await designService.getDesigns({
      limit: PAGE_SIZE,
      // A page number, not a record offset — verified against production traffic (§8).
      offset: page.value,
      query: appliedSearchTerm.value || undefined,
      sortField: 'CREATED',
      sortOrder: 'DESC',
    });
    designs.value = list || [];
    count.value = total;
  } catch {
    // The list is the whole screen, so a failure has to be said out loud rather than left as a
    // spinner that never stops.
    loadFailed.value = true;
    designs.value = [];
    count.value = 0;
  } finally {
    loading.value = false;
  }
};

const onPageChange = (nextPage: number) => {
  page.value = nextPage;
  fetchDesigns();
};

const search = () => {
  if (searchTerm.value === appliedSearchTerm.value) return;
  appliedSearchTerm.value = searchTerm.value;
  page.value = 0;
  fetchDesigns();
};

const clearSearch = () => {
  searchTerm.value = '';
  search();
};

onMounted(fetchDesigns);
</script>

<style lang="scss" scoped>
@import 'src/scss/styleguide/colors';
@import 'src/scss/constants';

.top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  margin: 0 0 24px 0;
}

.header {
  h2 {
    margin: 0 0 8px 0;
  }

  p {
    margin: 0;
    color: $grey60;
  }
}

.design-list {
  list-style: none;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 24px;
  padding: 0;
  margin: 0 0 24px 0;
}

.empty {
  color: $grey60;

  p {
    margin: 0 0 4px 0;
  }
}
</style>
